
import React, { useEffect } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useListingForm } from "@/context/ListingFormContext";
import { useQuery } from "@tanstack/react-query";
import { getCategories } from "@/lib/actions/categoryActions";
import SubcategorySelect from "@/components/ui/SubcategorySelect";

interface Category {
  id: number;
  name: string;
  slug: string;
}

const ListingCategorySection = () => {
  const { formData, errors, handleInputChange } = useListingForm();
  const [categories, setCategories] = React.useState<Category[]>([]);

  // Check if the selected category is a service category (no condition/delivery required)
  const isServiceCategory = ['8', '9', '10'].includes(formData.category);

  // Fetch categories using the categoryActions function
  const { isLoading: isLoadingCategories } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const data = await getCategories();
      setCategories(data || []);
      return data;
    },
  });

  // When category changes to a service category, set a default condition
  useEffect(() => {
    if (isServiceCategory && formData.condition === '') {
      handleInputChange('condition', 'new');
    }
  }, [formData.category]);

  // Handle subcategory change without causing loops
  const handleSubcategoryChange = (value: string) => {
    if (value !== formData.subcategory) {
      handleInputChange("subcategory", value);
    }
  };

  return (
    <>
      <div className="mb-4">
        <Label
          htmlFor="category"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Kategoria
        </Label>
        <Select
          value={formData.category}
          onValueChange={(value) => {
            handleInputChange("category", value);
          }}
        >
          <SelectTrigger id="category" className="w-full mt-1">
            <SelectValue placeholder="Zgjidhni kategorinë" />
          </SelectTrigger>
          <SelectContent>
            {isLoadingCategories ? (
              <SelectItem value="loading" disabled>
                Duke ngarkuar...
              </SelectItem>
            ) : categories.length > 0 ? (
              categories.map((category) => (
                <SelectItem key={category.id} value={category.id.toString()}>
                  {category.name}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="none" disabled>
                Nuk ka kategori
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        {errors.category && (
          <p className="mt-1 text-sm text-red-500">{errors.category}</p>
        )}
      </div>

      {/* Subcategory select field - show only when category is selected */}
      {formData.category && (
        <div className="mb-4">
          <Label
            htmlFor="subcategory"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Nënkategoria
          </Label>
          <SubcategorySelect
            value={formData.subcategory}
            onChange={handleSubcategoryChange}
            categoryId={formData.category}
          />
          {errors.subcategory && (
            <p className="mt-1 text-sm text-red-500">{errors.subcategory}</p>
          )}
        </div>
      )}

      {/* Only show condition field for non-service categories */}
      {!isServiceCategory && (
        <div className="mb-4">
          <Label
            htmlFor="condition"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Gjendja
          </Label>
          <Select
            value={formData.condition}
            onValueChange={(value) => handleInputChange("condition", value)}
          >
            <SelectTrigger id="condition" className="w-full mt-1">
              <SelectValue placeholder="Zgjidhni Gjendjen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">E re</SelectItem>
              <SelectItem value="like_new">Si e re</SelectItem>
              <SelectItem value="good">E mirë</SelectItem>
              <SelectItem value="used">E përdorur</SelectItem>
              <SelectItem value="needs_repair">Nevojitet riparim</SelectItem>
            </SelectContent>
          </Select>
          {errors.condition && (
            <p className="mt-1 text-sm text-red-500">{errors.condition}</p>
          )}
        </div>
      )}
    </>
  );
};

export default ListingCategorySection;
